<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, defineProps, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
/* Emits */
const emits = defineEmits(['closeModal']);
/* Props */
const props = defineProps({
    // La chiave gia' decisa dalla riga: qui si traduce e basta, cosi' la regola
    // su quale errore mostrare resta in un posto solo.
    errorKey: { required: true, type: String }
});
/* Data */
const { t } = useI18n();
const store = useStore();
const dialog = ref(null);
// L'elemento che aveva il fuoco all'apertura — il pulsante della riga: alla
// chiusura ci deve tornare, o chi naviga da tastiera riparte da capo dalla
// tabella.
const opener = typeof document !== 'undefined' ? document.activeElement : null;
/* Computed */
const message = computed( () => t(props.errorKey, store.state.locale));
/* Methods */
/**
 * Il fuoco resta dentro il popup finche' e' aperto: sotto c'e' il modale di
 * import, che in questo momento non e' raggiungibile col mouse.
 */
const focusables = () => Array.from(
    dialog.value?.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])') ?? []
);
const onKeydown = (e) =>
{
    if(e.key === 'Escape') return close();

    if(e.key !== 'Tab') return;

    const items = focusables();

    if(!items.length) return;

    const first = items[0];
    const last = items[items.length - 1];

    // Il fuoco e' finito fuori dal popup: riportarlo dentro, altrimenti il Tab
    // successivo se ne va sul modale sotto.
    if(!dialog.value?.contains(document.activeElement))
    {
        e.preventDefault();

        return (e.shiftKey ? last : first).focus();
    }

    if(e.shiftKey && document.activeElement === first)
    {
        e.preventDefault();
        last.focus();
    }
    else if(!e.shiftKey && document.activeElement === last)
    {
        e.preventDefault();
        first.focus();
    }
};
const close = () => emits('closeModal');
/* Hooks */
onMounted( async () =>
{
    document.addEventListener('keydown', onKeydown);

    await nextTick();
    focusables()[0]?.focus();
});
onUnmounted( () =>
{
    document.removeEventListener('keydown', onKeydown);
    opener?.focus?.();
});
</script>
<template>
    <div
        @click.prevent="close"
        class="fixed inset-0 p-6 bg-gray-1000 bg-opacity-90 z-40 overscroll-y-contain overflow-y-auto text-blue-500">
        <div class="flex flex-col w-full h-full items-center justify-center">
            <div
                @click.stop
                ref="dialog"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="import-row-error-title"
                aria-describedby="import-row-error-message"
                class="bg-white dark:bg-gray-900 dark:text-gray-400 border dark:border-gray-700 rounded p-5 flex flex-col space-y-4 w-full md:w-2/3 lg:w-1/2 2xl:w-1/3">
                <div class="flex items-start justify-between space-x-3">
                    <h2 id="import-row-error-title" class="text-xl flex items-center space-x-2 text-red-700 dark:text-red-300">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-6 h-6 fill-current flex-shrink-0" aria-hidden="true" focusable="false">
                            <path d="M12,2A10,10,0,1,0,22,12,10.011,10.011,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.009,8.009,0,0,1,12,20Zm.5-3.5a.75.75,0,1,1-.75-.75A.75.75,0,0,1,12.5,16.5ZM11.75,6.5a3,3,0,0,0-3,3,1,1,0,0,0,2,0,1,1,0,1,1,1,1,1,1,0,0,0-1,1v1.25a1,1,0,0,0,2,0v-.42a3,3,0,0,0-1-5.83Z"/>
                        </svg>
                        <span>{{ $t('importModal.errors.title', $store.state.locale) }}</span>
                    </h2>
                    <button
                        :title="$t('actions.close', $store.state.locale)"
                        :aria-label="$t('actions.close', $store.state.locale)"
                        @click.prevent="close"
                        class="bg-transparent min-w-min flex items-center justify-center transform hover:rotate-90 duration-300">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" class="w-4 fill-current text-gray-800 dark:text-gray-400" aria-hidden="true" focusable="false">
                                <path d="M34.5,32L62.2,4.2c0.7-0.7,0.7-1.8,0-2.5c-0.7-0.7-1.8-0.7-2.5,0L32,29.5L4.2,1.8c-0.7-0.7-1.8-0.7-2.5,0
                                    c-0.7,0.7-0.7,1.8,0,2.5L29.5,32L1.8,59.8c-0.7,0.7-0.7,1.8,0,2.5c0.3,0.3,0.8,0.5,1.2,0.5s0.9-0.2,1.2-0.5L32,34.5l27.7,27.8
                                    c0.3,0.3,0.8,0.5,1.2,0.5c0.4,0,0.9-0.2,1.2-0.5c0.7-0.7,0.7-1.8,0-2.5L34.5,32z"/>
                            </svg>
                    </button>
                </div>
                <p id="import-row-error-message" class="text-sm">
                    {{ message }}
                </p>
                <div class="flex items-center justify-end">
                    <button
                        @click.prevent="close"
                        class="px-5 py-3 font-medium leading-5 text-center text-white bg-blue-500 hover:bg-blue-300 dark:hover:bg-blue-700 rounded duration-300 lg:w-auto">
                        {{ $t('actions.close', $store.state.locale) }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
